import { useEffect, useId, useState } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { NumberInput } from '../ui/NumberInput';
import { bpToPct, pctToBp, type Version } from './types';
import { useUnsavedGuard } from '../ui/unsaved';

/** Some equipment line has a discount other than the version's ("Ustaw" then sets the same value again for all). */
function mixedDiscounts(version: Version): boolean {
  const target = version.discountBp ?? 0;
  return version.sections.some((s) => s.kind === 'equipment' && s.lines.some((l) => l.discountBp !== undefined && l.discountBp !== target));
}

/**
 * "Rabat na urządzenia [ 15 ] %" with "Ustaw" of a draft (owner 20.10: "pole ustaw rabat"; 2026-10-23: "rabat tez
 * przenies do info" — it lives in "ⓘ Info", not over the table). Setting it puts the percentage on every equipment line
 * of the version (overwriting what was typed per line) and on the new ones; services and transport keep theirs. Set
 * again, it applies to all again. No "are you sure". Sends only the percentage and the version (rule 7a). Who may see
 * the discount (`discountBp`) is decided by the caller.
 */
export function EquipmentDiscount({
  version,
  onDiscountSet,
  onConflict,
}: {
  version: Version;
  /** The server took the discount: read the version again. */
  onDiscountSet: () => void;
  onConflict: () => void;
}) {
  const hintId = useId();
  const current = bpToPct(version.discountBp ?? 0);
  const [value, setValue] = useState(current);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);
  // the server (or another person) changed it: show the version's value
  useEffect(() => setValue(current), [current]);
  const mixed = mixedDiscounts(version);
  const unchanged = pctToBp(value) === (version.discountBp ?? 0);

  async function apply(): Promise<boolean> {
    const discountBp = pctToBp(value);
    if (discountBp === null) {
      setDone('');
      setError('Rabat od 0 do 100%, np. 10');
      return false;
    }
    if (busy || (unchanged && !mixed)) return true;
    setBusy(true);
    try {
      await api(`/api/quotes/versions/${version.id}/equipment-discount`, { method: 'POST', body: { discountBp, version: version.version } });
      setError('');
      setDone(discountBp === 0 ? 'Urządzenia bez rabatu.' : `Rabat ${bpToPct(discountBp)}% ustawiony na wszystkie urządzenia.`);
      onDiscountSet();
      return true;
    } catch (e) {
      setDone('');
      if (e instanceof ApiError && e.status === 409) {
        setError('Ktoś w tym czasie zmienił kosztorys. Pokazuję aktualne wartości — ustaw rabat jeszcze raz.');
        onConflict();
      } else setError(e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : 'Nie udało się ustawić rabatu');
      return false;
    } finally {
      setBusy(false);
    }
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({ dirty: value.trim() !== current, save: apply });

  return (
    <form
      ref={guardRef}
      className="q-discount"
      data-feature-id="quotes.equipment_discount"
      title="Ustawia rabat wszystkim pozycjom sprzętowym tej wersji i nowym, które dodasz. Usługi i transport bez zmian. Pojedynczą pozycję zmienisz w kolumnie „Rabat %”."
      onSubmit={(e) => {
        e.preventDefault();
        void apply();
      }}
    >
      <NumberInput
        aria-label="Rabat na urządzenia (%)"
        aria-describedby={hintId}
        aria-invalid={error ? true : undefined}
        value={value}
        onValueChange={(v) => {
          setValue(v);
          setError('');
          setDone('');
        }}
      />
      <span aria-hidden="true">%</span>
      <button className="btn small" type="submit" disabled={busy || (unchanged && !mixed)}>
        Ustaw
      </button>
      <span id={hintId} className="visually-hidden">
        Ustawia rabat wszystkim pozycjom sprzętowym tej wersji i nowym. Usługi i transport bez zmian.
      </span>
      {/* a line changed by hand after "Ustaw": the note replaces the older "ustawiony" message */}
      {mixed && <span className="q-discount-note">niektóre pozycje mają inny rabat</span>}
      <span className="q-discount-note" role="status">
        {mixed ? '' : done}
      </span>
      {error && (
        <span className="field-error q-discount-error" role="alert">
          {error}
        </span>
      )}
    </form>
  );
}
